import React, { useState, useEffect } from 'react';
import { Search, Plus, Upload, X, Eye, Edit2, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

/**
 * LicenciasEstudiante - Solicitud y seguimiento de licencias
 * Flujo: Estudiante solicita → Jefatura aprueba → Se registra en módulo docente
 */
const LicenciasEstudiante = () => {
    const [licencias, setLicencias] = useState([]);
    const [asignaturas, setAsignaturas] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedLicencia, setSelectedLicencia] = useState(null);
    const [editingLicencia, setEditingLicencia] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Formulario de nueva licencia
    const [formData, setFormData] = useState({
        id_asignatura: '',
        docente: '',
        tipo_solicitud: '',
        motivo_solicitud: '',
        fecha: '',
        comentarios: ''
    });
    const [respaldo, setRespaldo] = useState(null);

    // Reglas de validación
    const RULES = {
        'Permiso Institucional': {
            'Presentacion EBID': { minDays: 0, limit: Infinity }
        },
        'Licencia Personal': {
            'Salud': { postHours: 48, limit: Infinity },
            'Trabajo': { minDays: 1, limit: 3 },
            'Trabajo Artístico Local': { minDays: 1, limit: 2 },
            'Trabajo Artístico Nacional': { minDays: 2, limit: 1 },
            'Trabajo Artístico Internacional': { minMonths: 1, limit: 1 },
            'Estudio': { minDays: 1, limit: 3 }
        },
        'Tolerancia de ingreso o salida': {
            'Trabajo': { minWeeks: 1, limit: Infinity },
            'Estudio': { minWeeks: 1, limit: Infinity }
        },
        'Tolerancia en clases': {
            'Salud': { postHours: 48, limit: Infinity }
        }
    };

    const motivosPorTipo = Object.keys(RULES).reduce((acc, key) => {
        acc[key] = Object.keys(RULES[key]);
        return acc;
    }, {});

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

            // Cargar licencias del estudiante
            const licRes = await fetch(`${API_URL}/estudiante/licencias`, { headers });
            if (licRes.ok) {
                const licData = await licRes.json();
                setLicencias(licData.licencias || []);
            } else {
                // Datos de ejemplo
                setLicencias([
                    { id_licencia: 1, asignatura: 'HISTORIA DE LA DANZA', docente: 'Lic. García', tipo_solicitud: 'Licencia Personal', hora_inicio: '09:00', hora_fin: '12:00', comentarios: 'Cita médica', estado: 'Pendiente', observaciones_jefatura: '' },
                    { id_licencia: 2, asignatura: 'TÉCNICA DE BALLET', docente: 'Prof. López', tipo_solicitud: 'Permiso Institucional', hora_inicio: '14:00', hora_fin: '16:00', comentarios: 'Presentación', estado: 'Aprobado', observaciones_jefatura: '' },
                    { id_licencia: 3, asignatura: 'FOLKLORE', docente: 'Lic. Pérez', tipo_solicitud: 'Tolerancia', hora_inicio: '10:00', hora_fin: '11:00', comentarios: 'Trabajo', estado: 'Observado', observaciones_jefatura: 'Presentar justificativo firmado' }
                ]);
            }

            // Cargar asignaturas activas del estudiante
            const asigRes = await fetch(`${API_URL}/estudiante/asignaturas-activas`, { headers });
            if (asigRes.ok) {
                const asigData = await asigRes.json();
                setAsignaturas(asigData.asignaturas || []);
            } else {
                setAsignaturas([
                    { id_asignatura: 1, nombre: 'Artes', docente: 'Jose Pinto' },
                    { id_asignatura: 2, nombre: 'Historia de la Danza', docente: 'María García' },
                    { id_asignatura: 3, nombre: 'Técnica de Ballet', docente: 'Carlos López' }
                ]);
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAsignaturaChange = (e) => {
        const id = e.target.value;
        const asig = asignaturas.find(a => a.id_asignatura == id);
        setFormData(prev => ({
            ...prev,
            id_asignatura: id,
            docente: asig?.docente || ''
        }));
    };

    const handleTipoChange = (e) => {
        setFormData(prev => ({
            ...prev,
            tipo_solicitud: e.target.value,
            motivo_solicitud: '' // Reset motivo cuando cambia el tipo
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) setRespaldo(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errorValidation = validateRequest();
        if (errorValidation) {
            alert(errorValidation);
            return;
        }

        setSubmitting(true);

        try {
            const token = localStorage.getItem('token');
            const formDataToSend = new FormData();

            // Enviar campos (sin asignatura, expandido en backend)
            formDataToSend.append('tipo_solicitud', formData.tipo_solicitud);
            formDataToSend.append('motivo_solicitud', formData.motivo_solicitud);
            formDataToSend.append('fecha_inicio', formData.fecha);
            formDataToSend.append('fecha_fin', formData.fecha_fin || formData.fecha);
            formDataToSend.append('comentarios', formData.comentarios);

            if (respaldo) formDataToSend.append('respaldo', respaldo);

            const isEditing = !!editingLicencia;
            const url = isEditing
                ? `${API_URL}/estudiante/licencias/${editingLicencia.id_licencia}`
                : `${API_URL}/estudiante/licencias`;
            const method = isEditing ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                body: formDataToSend
            });

            const result = await response.json();

            if (response.ok) {
                if (!isEditing && result.licencias && result.licencias.length === 0) {
                    alert('Atención: El sistema no encontró clases programadas en las fechas seleccionadas, por lo que no se generaron licencias.');
                } else if (isEditing) {
                    alert('Licencia actualizada con éxito.');
                }
                
                // FIX #8: Recargar datos inmediatamente para reflejar cambios
                await loadData();
                
                // Resetear formulario
                setFormData({ tipo_solicitud: '', motivo_solicitud: '', fecha: '', fecha_fin: '', comentarios: '' });
                setEditingLicencia(null);
                setRespaldo(null);
                setShowModal(false);
            } else {
                alert('Error: ' + result.message);
            }
        } catch (error) {
            console.error('Error al enviar solicitud:', error);
        } finally {
            setSubmitting(false);
        }
    };

    // Función para agrupar licencias por solicitud (mismo tipo, motivo y comentarios)
    const agruparLicencias = (lista) => {
        const grupos = {};
        
        lista.forEach(lic => {
            // Creamos una clave única para agrupar (por tipo, motivo, comentarios y estado)
            const key = `${lic.tipo_solicitud}-${lic.motivo_solicitud}-${lic.comentarios}-${lic.estado}`;
            
            if (!grupos[key]) {
                grupos[key] = {
                    ...lic,
                    fecha_desde: lic.fecha_licencia || lic.fecha_hora_inicio || lic.fecha,
                    fecha_hasta: lic.fecha_licencia || lic.fecha_hora_fin || lic.fecha,
                    originales: [lic]
                };
            } else {
                const fechaActual = lic.fecha_licencia || lic.fecha_hora_inicio || lic.fecha;
                if (new Date(fechaActual) < new Date(grupos[key].fecha_desde)) {
                    grupos[key].fecha_desde = fechaActual;
                }
                if (new Date(fechaActual) > new Date(grupos[key].fecha_hasta)) {
                    grupos[key].fecha_hasta = fechaActual;
                }
                grupos[key].originales.push(lic);
            }
        });
        
        return Object.values(grupos);
    };

    // Filtrar licencias por búsqueda y luego agruparlas
    const licenciasAgrupadas = agruparLicencias(licencias.filter(l =>
        l.asignatura?.toLowerCase().includes(search.toLowerCase()) ||
        l.docente?.toLowerCase().includes(search.toLowerCase()) ||
        l.tipo_solicitud?.toLowerCase().includes(search.toLowerCase())
    ));

    const getEstadoColor = (estado) => {
        const e = (estado || 'pendiente').toLowerCase();
        switch (e) {
            case 'aprobado': return 'bg-green-200 text-green-800';
            case 'observado': return 'bg-red-200 text-red-800';
            default: return 'bg-gray-200 text-gray-700';
        }
    };

    const validateRequest = () => {
        const { tipo_solicitud, motivo_solicitud, fecha, fecha_fin } = formData;
        if (!RULES[tipo_solicitud] || !RULES[tipo_solicitud][motivo_solicitud]) return null;

        const rule = RULES[tipo_solicitud][motivo_solicitud];
        const start = new Date(fecha + 'T00:00:00');
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        // Validar anticipación
        const diffTime = start - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (rule.minMonths && diffDays < rule.minMonths * 30) return `Debe solicitarse con ${rule.minMonths} mes(es) de anticipación.`;
        if (rule.minWeeks && diffDays < rule.minWeeks * 7) return `Debe solicitarse con ${rule.minWeeks} semana(s) de anticipación.`;
        if (rule.minDays !== undefined && diffDays < rule.minDays) return `Debe solicitarse con ${rule.minDays} día(s) de anticipación.`;

        // Validar retrospectiva (Salud)
        if (rule.postHours) {
            // Si start es > now, ok. Si start < now, no más de 48h atrás.
            if (diffDays < 0) {
                const hoursLate = Math.abs(diffTime) / (1000 * 60 * 60);
                if (hoursLate > rule.postHours) return `Solo se puede justificar hasta ${rule.postHours} horas después del incidente.`;
            }
        }

        // Validar Cantidad Anual
        if (rule.limit !== Infinity) {
            const count = licencias.filter(l =>
                l.tipo_solicitud === tipo_solicitud &&
                l.motivo_solicitud === motivo_solicitud &&
                l.estado !== 'Rechazado' &&
                new Date(l.fecha_licencia).getFullYear() === new Date().getFullYear()
            ).length;

            if (count >= rule.limit) return `Has alcanzado el límite anual de ${rule.limit} solicitudes para este motivo.`;
        }

        return null;
    };

    if (loading) {
        return (
            <div className="p-6 flex justify-center items-center">
                <Loader className="animate-spin mr-2" /> Cargando licencias...
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-bold text-gray-800">Estudiante &gt; Solicitud Permiso</h1>

            </div>

            {/* Condiciones de Licencias */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p className="font-medium text-blue-800 mb-1">Condiciones para solicitar licencias:</p>
                <ul className="text-blue-700 list-disc list-inside space-y-0.5 text-xs">
                    <li><strong>Permiso Institucional:</strong> Presentación EBID — sin límite de solicitudes.</li>
                    <li><strong>Licencia Personal — Salud:</strong> Hasta 48h después del incidente, sin límite anual.</li>
                    <li><strong>Licencia Personal — Trabajo:</strong> Mínimo 1 día de anticipación, máximo 3 al año.</li>
                    <li><strong>Licencia Personal — Trabajo Artístico Local:</strong> Mínimo 1 día, máximo 2 al año.</li>
                    <li><strong>Licencia Personal — Trabajo Artístico Nacional:</strong> Mínimo 2 días, máximo 1 al año.</li>
                    <li><strong>Licencia Personal — Trabajo Artístico Internacional:</strong> Mínimo 1 mes, máximo 1 al año.</li>
                    <li><strong>Licencia Personal — Estudio:</strong> Mínimo 1 día, máximo 3 al año.</li>
                    <li><strong>Tolerancia de ingreso/salida:</strong> Mínimo 1 semana de anticipación (Trabajo/Estudio).</li>
                    <li><strong>Tolerancia en clases — Salud:</strong> Hasta 48h después del incidente.</li>
                    <li>Las licencias aprobadas se reflejan automáticamente en el registro del docente.</li>
                    <li>Adjuntar respaldo documentado para licencias por salud o viaje.</li>
                </ul>
            </div>

            {/* Barra de acciones */}
            <div className="flex items-center gap-3 mb-6">
                {/* Botón añadir */}
                <button
                    onClick={() => {
                        setEditingLicencia(null);
                        setFormData({ tipo_solicitud: '', motivo_solicitud: '', fecha: '', fecha_fin: '', comentarios: '' });
                        setRespaldo(null);
                        setShowModal(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition"
                    title="Añadir Licencia"
                >
                    <Plus size={18} /> Añadir Licencia
                </button>

                {/* Buscador */}
                <div className="relative flex-1 max-w-md">
                    <input
                        type="text"
                        placeholder="Buscar por tipo, motivo o comentario..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500"
                    />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
            </div>

            {/* Tabla de licencias */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="px-3 py-3 text-center font-semibold border">No</th>
                            <th className="px-3 py-3 text-center font-semibold border">Tipo / Motivo</th>
                            <th className="px-3 py-3 text-center font-semibold border">Desde</th>
                            <th className="px-3 py-3 text-center font-semibold border">Hasta</th>
                            <th className="px-3 py-3 text-center font-semibold border">Comentarios</th>
                            <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                            <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                            <th className="px-3 py-3 text-center font-semibold border">Mensaje Jefatura</th>
                        </tr>
                    </thead>
                    <tbody>
                        {licenciasAgrupadas.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                                    No hay licencias registradas
                                </td>
                            </tr>
                        ) : (
                            licenciasAgrupadas.map((lic, idx) => (
                                <tr key={lic.id_licencia} className="border-b hover:bg-gray-50">
                                    <td className="px-3 py-3 text-center text-gray-500 border">{idx + 1}</td>
                                    <td className="px-3 py-3 border">
                                        <div className="font-bold text-gray-800">{lic.tipo_solicitud}</div>
                                        <div className="text-xs text-blue-600">{lic.motivo_solicitud}</div>
                                    </td>
                                    <td className="px-3 py-3 text-center font-medium border">
                                        {lic.fecha_desde ? new Date(lic.fecha_desde).toLocaleDateString('es-BO') : '-'}
                                    </td>
                                    <td className="px-3 py-3 text-center font-medium border">
                                        {lic.fecha_hasta ? new Date(lic.fecha_hasta).toLocaleDateString('es-BO') : '-'}
                                    </td>
                                    <td className="px-3 py-3 text-gray-600 italic border">"{lic.comentarios}"</td>
                                    <td className="px-3 py-3 text-center border">
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => { setSelectedLicencia(lic); setShowViewModal(true); }}
                                                className="p-1 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition"
                                                title="Ver detalles"
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const esPendiente = !lic.estado || lic.estado.toLowerCase() === 'pendiente';
                                                    if (esPendiente) {
                                                        const asigEncontrada = asignaturas.find(a =>
                                                            a.nombre === lic.asignatura ||
                                                            a.nombre?.toLowerCase() === lic.asignatura?.toLowerCase()
                                                        );

                                                        setEditingLicencia(lic);
                                                        setFormData({
                                                            id_asignatura: asigEncontrada?.id_asignatura || '',
                                                            docente: lic.docente || '',
                                                            tipo_solicitud: lic.tipo_solicitud || '',
                                                            motivo_solicitud: lic.motivo_solicitud || '',
                                                            fecha: (lic.fecha_desde || '').split('T')[0],
                                                            fecha_fin: (lic.fecha_hasta || '').split('T')[0],
                                                            comentarios: lic.comentarios || ''
                                                        });
                                                        setShowModal(true);
                                                    } else {
                                                        alert('Solo puedes editar licencias en estado Pendiente');
                                                    }
                                                }}
                                                className={`p-1 rounded-full transition ${(!lic.estado || lic.estado.toLowerCase() === 'pendiente') ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                                                title={(!lic.estado || lic.estado.toLowerCase() === 'pendiente') ? 'Editar' : 'No editable'}
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 text-center border">
                                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getEstadoColor(lic.estado)}`}>
                                            {lic.estado || 'Pendiente'}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3 text-xs text-gray-500 max-w-[200px] truncate border">
                                        {lic.observaciones_jefatura || lic.razon_rechazo || ''}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* ========== MODAL SOLICITUD ========== */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden">
                        {/* Header del modal */}
                        <div className="flex justify-between items-center p-4 border-b">
                            <h2 className="text-lg font-bold text-blue-900">
                                {editingLicencia ? 'Editar Licencia' : 'Nueva Solicitud de Licencia'}
                            </h2>
                            <button onClick={() => { setShowModal(false); setEditingLicencia(null); }} className="text-gray-500 hover:text-gray-700">
                                <X size={24} />
                            </button>
                        </div>

                        {/* Formulario */}
                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* Tipo Solicitud */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Tipo Solicitud</label>
                                    <select
                                        name="tipo_solicitud"
                                        value={formData.tipo_solicitud}
                                        onChange={handleTipoChange}
                                        className="w-full px-3 py-2 border rounded-md text-sm"
                                        required
                                    >
                                        <option value="">-- Seleccione tipo --</option>
                                        {Object.keys(motivosPorTipo).map(tipo => (
                                            <option key={tipo} value={tipo}>{tipo}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Motivo Solicitud */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Motivo Solicitud</label>
                                    <select
                                        name="motivo_solicitud"
                                        value={formData.motivo_solicitud}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border rounded-md text-sm"
                                        required
                                        disabled={!formData.tipo_solicitud}
                                    >
                                        <option value="">-- Seleccione motivo --</option>
                                        {(motivosPorTipo[formData.tipo_solicitud] || []).map(m => (
                                            <option key={m} value={m}>{m}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* Desde */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Desde</label>
                                    <input
                                        type="date"
                                        name="fecha"
                                        value={formData.fecha}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border rounded-md text-sm"
                                        required
                                    />
                                </div>
                                {/* Hasta */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Hasta</label>
                                    <input
                                        type="date"
                                        name="fecha_fin"
                                        value={formData.fecha_fin || formData.fecha} // Default to start date if empty
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border rounded-md text-sm"
                                        required
                                        min={formData.fecha}
                                    />
                                </div>
                            </div>

                            {/* Info Box */}
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-start gap-3">
                                <div className="p-1 bg-blue-100 rounded-full text-blue-600 mt-0.5">
                                    <Eye size={16} />
                                </div>
                                <p className="text-xs text-blue-800">
                                    La licencia se aplicará automáticamente a todas las clases programadas del estudiante dentro del rango seleccionado.
                                </p>
                            </div>

                            {/* Respaldo */}
                            <div className="mb-4">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Respaldo</label>
                                <div className="relative">
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        className="w-full px-3 py-2 border rounded-md text-sm pr-10"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                    />
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                                        <Upload size={16} />
                                    </div>
                                </div>
                            </div>

                            {/* Comentarios */}
                            <div className="mb-6">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Comentarios</label>
                                <textarea
                                    name="comentarios"
                                    value={formData.comentarios}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                    rows="3"
                                    placeholder="Escriba sus comentarios aquí..."

                                ></textarea>
                            </div>

                            {/* Footer Buttons */}
                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-6 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 flex items-center gap-2 font-medium"
                                >
                                    {submitting ? <Loader className="animate-spin" size={18} /> : null} Enviar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ========== MODAL VER DETALLES ========== */}
            {
                showViewModal && selectedLicencia && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                            {/* Header del modal */}
                            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
                                <h2 className="text-xl font-bold text-blue-900">Detalles de la Licencia</h2>
                                <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700">
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Contenido */}
                            <div className="p-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm text-gray-500">Asignatura</label>
                                        <p className="font-medium">{selectedLicencia.asignatura || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-500">Docente</label>
                                        <p className="font-medium">{selectedLicencia.docente || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-500">Tipo Solicitud</label>
                                        <p className="font-medium">{selectedLicencia.tipo_solicitud || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-500">Motivo</label>
                                        <p className="font-medium">{selectedLicencia.motivo_solicitud || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-500">Válido Desde</label>
                                        <p className="font-medium">
                                            {selectedLicencia.fecha_desde ? new Date(selectedLicencia.fecha_desde).toLocaleDateString('es-BO') : '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-500">Válido Hasta</label>
                                        <p className="font-medium">
                                            {selectedLicencia.fecha_hasta ? new Date(selectedLicencia.fecha_hasta).toLocaleDateString('es-BO') : '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-500">Estado</label>
                                        <p className={`font-bold mt-1 inline-block px-3 py-1 rounded-full text-xs uppercase tracking-widest ${getEstadoColor(selectedLicencia.estado)}`}>
                                            {selectedLicencia.estado || 'Pendiente'}
                                        </p>
                                    </div>
                                    <div className="col-span-2 mt-2 pt-2 border-t">
                                        <label className="block text-sm text-gray-500 mb-2">Asignaturas Afectadas</label>
                                        <div className="flex flex-wrap gap-2">
                                            {[...new Set((selectedLicencia.originales || []).map(o => o.asignatura))].map((asig, i) => (
                                                <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-100 text-xs font-medium">
                                                    {asig}
                                                </span>
                                            ))}
                                            {!(selectedLicencia.originales) && <span className="text-gray-400 italic">No especificado</span>}
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm text-gray-500">Comentarios</label>
                                        <p className="font-medium">{selectedLicencia.comentarios || '-'}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm text-gray-500">Observaciones Jefatura</label>
                                        <p className="font-medium">{selectedLicencia.observaciones_jefatura || selectedLicencia.razon_rechazo || '-'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end px-6 py-4 border-t">
                                <button
                                    onClick={() => setShowViewModal(false)}
                                    className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default LicenciasEstudiante;